// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PropTypes from 'prop-types';

// Data
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useNavigate, useMatch } from 'react-router-dom';

// Components
import MapNav from "nextus/Sidenavs/MapNav";
import ExploringMapDevices from "nextus/Maps/ExploringMaps/Devices";

// API/MODELS
import groupAPI from "api/groups";
import bksAPI from "api/bks";
import clearwaysAPI from "modules/clearways/api";
import userAPI from "api/user";

// Map
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

// helpers
import { processDevicesData } from "helper/device";
import MapMenuContent from "nextus/MapMenuContent";
import { processEventsData } from "helper/event";
import { useSoftUIController } from "context";

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function wrapper(map, content) {
    return (
        <SoftBox sx={{ width: '100%', height: '100%' }}>
            {map}
            {content}
        </SoftBox>
    )
}

function ExploreMapDevices({ map, navTitle, setNavTitle, sidebarState, setSidebarState }) {
    groupAPI.loadToken(useAuth0());
    bksAPI.loadToken(useAuth0());
    clearwaysAPI.loadToken(useAuth0());
    userAPI.loadToken(useAuth0());

    const { user } = useAuth0();

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [menuToggled, setMenuToggled] = useState(true);
    const [mapPadding, setMapPadding] = useState([600, 250]);

    const [controller, dispatch] = useSoftUIController();
    const { miniSidenav, hiddenSidenav, direction, layout, openConfigurator, sidenavColor, deviceStyle } = controller;

    const [eventbarState, setEventbarState] = useState('closed');

    const { group_id, subgroup_id } = useMatch('explore/groups/:group_id/:subgroup_id').params;

    const group = useQuery({
        queryKey: ['getGroupWithID', group_id],
        queryFn: async () => {
            return await groupAPI.getGroupWithID(group_id);
        }
    });

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await bksAPI.getAllDevices()
        }
    });

    const events = useQuery({
        queryKey: ['getAllClearways'],
        queryFn: async () => {
            return await clearwaysAPI.getAllClearways();
        },
        // staleTime: 20 * 1000
    });

    const selected_user = useQuery({
        queryKey: ['getUserByEmail', user.email.toLowerCase()],
        queryFn: async () => {
            return await userAPI.getUserByEmail(user.email.toLowerCase());
        },
        // staleTime: 20 * 1000
    });

    useEffect(() => {
        // console.log('explore_map_view_devices')
        setSidebarState('exploring');
        setNavTitle(['explore map']);

        if (deviceStyle == 'mobile') {
            setMapPadding([0, 0]);
            // setSidebarOpen(false);
        }
        else {
            if (!sidebarOpen) {
                if (eventbarState == 'open') {
                    setMapPadding([0, 250]);
                }
                else {
                    setMapPadding([0, 0]);
                }
                setSidebarOpen(false);
            }
            else {
                if (eventbarState == 'open') {
                    setMapPadding([600, 250]);
                }
                else {
                    setMapPadding([600, 0]);
                }
                setSidebarOpen(true);
            }
        }

    }, [sidebarOpen, navTitle, deviceStyle]);

    if (group.isLoading || devices.isLoading || selected_user.isLoading || events.isLoading) {
        return wrapper('Loading...');
    }

    if (group.error || devices.error || selected_user.error) {
        if (group.error) {
            var error = group.error;
        }
        else if (devices.error) {
            var error = devices.error;
        }
        else if (selected_user.error) {
            var error = selected_user.error;
        }
        else if (events.error) {
            var error = events.error;
        }
        return wrapper("An error has occurred: " + error.message);
    }

    if (!group.data || !devices.data || !selected_user.data) {
        return wrapper('Loading...');
    }

    // if (events.isLoading) {
    //     return wrapper((<ExploringMapDevices map={map} mapPadding={mapPadding} user_role={selected_user.data.user.user_type} group={group.data} selected_group={selected_group} sidebarOpen={sidebarOpen} menuToggled={menuToggled} setMenuToggled={setMenuToggled} extraPadding={eventbarState == 'open'} />), ('Loading...'));
    // }

    // if (events.error) {
    //     return wrapper((<ExploringMapDevices map={map} mapPadding={mapPadding} user_role={selected_user.data.user.user_type} group={group.data} selected_group={selected_group} sidebarOpen={sidebarOpen} menuToggled={menuToggled} setMenuToggled={setMenuToggled} extraPadding={eventbarState == 'open'} />), ("An error has occurred: " + events.error.message));
    // }

    // if (!events.data) {
    //     return wrapper((<ExploringMapDevices map={map} mapPadding={mapPadding} user_role={selected_user.data.user.user_type} group={group.data} selected_group={selected_group} sidebarOpen={sidebarOpen} menuToggled={menuToggled} setMenuToggled={setMenuToggled} extraPadding={eventbarState == 'open'} />), ('Loading...'));
    // }

    var events_data = processEventsData(events.data);
    events_data = events_data.filter(function (event) {
        var ret = false;
        event.groups.forEach(function (group) {
            if (group.group_id == subgroup_id) {
                ret = true;
            }
        });

        return ret;
    });

    var selected_group = {};
    if (group.data.subgroups.length > 0) {
        selected_group = (group.data.subgroups.filter(group => subgroup_id == group.group_id))[0];
    }
    else {
        selected_group = group.data;
    }

    // console.log('selected', selected_group)

    var selected_group_devices = devices.data.filter(function (row) {
        return selected_group.uuids.indexOf(row.uuid) > -1;
    });

    selected_group.devices = processDevicesData(selected_group_devices);
    selected_group.all_updated = true;
    selected_group.devices_monitored = 0;
    for (var device of selected_group.devices) {
        if (device.connectivity == 'offline') {
            selected_group.all_updated = false;
        }
        if (device.battery <= 20) {
            selected_group.devices_monitored++;
        }
    }

    return (
        <SoftBox sx={{ width: '100%', height: '100%' }}>
            <ExploringMapDevices map={map} mapPadding={mapPadding} user_role={selected_user.data.user.user_type} group={group.data} selected_group={selected_group} sidebarOpen={sidebarOpen} menuToggled={menuToggled} setMenuToggled={setMenuToggled} extraPadding={events_data.length > 0} deviceStyle={deviceStyle} />
            <MapMenuContent sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} groupPreviewID={selected_group.group_id} sidebarState={'exploring'} events={events_data} eventbarState={events_data.length > 0 ? 'open' : 'closed'} setEventbarState={setEventbarState} deviceStyle={deviceStyle} />
        </SoftBox>
    );
}

ExploreMapDevices.propTypes = {
    map: PropTypes.object,
    navTitle: PropTypes.array,
    setNavTitle: PropTypes.func,
    sidebarState: PropTypes.string,
    setSidebarState: PropTypes.func
}

export default ExploreMapDevices;